/**
 * Чертёж в hero главной: квартира сама рисуется штрихом в 2D, потом наклоняется,
 * стены вырастают в объём, и чертёж перетекает в настоящий рендер этой же квартиры —
 * тот же сценарий «план → 3D», что и в планировщике.
 *
 * Без three.js: пол — один SVG, стены — стопка одинаковых SVG-срезов, разнесённых
 * по высоте `translateZ` (CSS 3D, считает видеокарта), а финальный кадр — WebP из
 * 3D-вида планировщика. Переключатель «2D / 3D» (как в планировщике) меняет только атрибут `data-view`,
 * всё остальное — переходы в `landing.css`.
 */
import { useEffect, useState } from "react";
import { UIKit } from "@/shared/ui";
import {
	formatArea,
	formatMm,
	PLAN_CABLE,
	PLAN_DOORS,
	PLAN_FURNITURE,
	PLAN_ROOMS,
	PLAN_SOCKETS,
	PLAN_VIEWBOX,
	PLAN_WALLS,
	PLAN_WINDOWS,
	planBounds,
	roomArea,
	totalArea,
	WALL_SLICES,
} from "../logic";
import { DimensionLine } from "./DimensionLine";

type View = "plan" | "3d";

/** Через сколько после начала рисования план сам поднимается в 3D. */
const AUTO_3D_MS = 3_600;

/** Рендер квартиры для вида 3D — из `public/`, рядом с главной ничего не грузит. */
const HERO_RENDER = "/landing/hero-3d.webp";

const VIEWBOX = `0 0 ${PLAN_VIEWBOX.w} ${PLAN_VIEWBOX.h}`;
const delay = (seconds: number) => ({ "--d": `${seconds}s` }) as React.CSSProperties;

/** Срез стен: все стены одним цветом, без обводки. */
const WallSlice = ({ index }: { index: number }) => (
	<svg
		className="lp-layer lp-slice"
		viewBox={VIEWBOX}
		aria-hidden="true"
		style={{ "--i": index + 1 } as React.CSSProperties}
	>
		{PLAN_WALLS.map((wall) => (
			<rect key={`${wall.x}-${wall.y}`} x={wall.x} y={wall.y} width={wall.w} height={wall.h} />
		))}
	</svg>
);

export const HeroPlan = () => {
	const [view, setView] = useState<View>("plan");
	const [touched, setTouched] = useState(false);
	const bounds = planBounds();

	useEffect(() => {
		if (touched) return;
		const timer = window.setTimeout(() => setView("3d"), AUTO_3D_MS);
		return () => window.clearTimeout(timer);
	}, [touched]);

	const choose = (next: View) => {
		setTouched(true);
		setView(next);
	};

	return (
		<figure className="lp-stage" data-view={view}>
			<div className="lp-scene">
				<div className="lp-tilt">
					{/* Пол: помещения, проёмы, мебель, электрика, размеры */}
					<svg className="lp-layer" viewBox={VIEWBOX} aria-hidden="true">
						<defs>
							<pattern
								id="lp-hatch"
								width="5"
								height="5"
								patternUnits="userSpaceOnUse"
								patternTransform="rotate(45)"
							>
								<rect width="5" height="5" className="lp-hatch-bg" />
								<path d="M0 0V5" className="lp-hatch-line" />
							</pattern>
						</defs>

						{PLAN_ROOMS.map((room, i) => (
							<rect
								key={room.name}
								className={`lp-fade lp-floor lp-floor--${room.floor}`}
								style={delay(1.3 + i * 0.08)}
								x={room.x}
								y={room.y}
								width={room.w}
								height={room.h}
							/>
						))}

						<g className="lp-furniture">
							{PLAN_FURNITURE.map((d, i) => (
								<path
									key={d}
									className="lp-draw"
									d={d}
									pathLength={1}
									style={delay(1.9 + i * 0.05)}
								/>
							))}
						</g>

						<g className="lp-doors">
							{PLAN_DOORS.map((d, i) => (
								<path
									key={d}
									className="lp-draw"
									d={d}
									pathLength={1}
									style={delay(1.2 + i * 0.1)}
								/>
							))}
						</g>

						<g>
							<path className="lp-draw lp-cable" d={PLAN_CABLE} pathLength={1} style={delay(2.5)} />
							{PLAN_SOCKETS.map((socket, i) => (
								<g
									key={`${socket.x}-${socket.y}`}
									className="lp-pop lp-socket"
									style={delay(2.6 + i * 0.12)}
									transform={`translate(${socket.x} ${socket.y}) rotate(${socket.rotate})`}
								>
									<path d="M-5 0A5 5 0 0 0 5 0Z" />
								</g>
							))}
						</g>

						<g>
							{PLAN_ROOMS.map((room, i) => (
								<g key={room.name} className="lp-fade" style={delay(1.6 + i * 0.1)}>
									<text
										className="lp-room-name"
										x={room.x + room.w / 2}
										y={room.y + room.h / 2 - 3}
									>
										{room.name.toUpperCase()}
									</text>
									<text
										className="lp-room-area"
										x={room.x + room.w / 2}
										y={room.y + room.h / 2 + 9}
									>
										{formatArea(roomArea(room))} м²
									</text>
								</g>
							))}
						</g>

						{/* Снаружи — только габарит, внутри — размер в чистоте */}
						<DimensionLine
							from={bounds.x}
							to={bounds.x + bounds.w}
							at={bounds.y - 18}
							base={bounds.y - 2}
							label={formatMm(bounds.w)}
							delay={1.5}
							className="lp-outer-dims"
						/>
						<DimensionLine
							vertical
							from={bounds.y}
							to={bounds.y + bounds.h}
							at={bounds.x - 16}
							base={bounds.x - 2}
							label={formatMm(bounds.h)}
							delay={1.6}
							className="lp-outer-dims"
						/>
						<DimensionLine
							from={PLAN_ROOMS[4].x}
							to={PLAN_ROOMS[4].x + PLAN_ROOMS[4].w}
							at={PLAN_ROOMS[4].y + 14}
							base={PLAN_ROOMS[4].y}
							label={formatMm(PLAN_ROOMS[4].w)}
							delay={2.1}
						/>
					</svg>

					{/* Стены: срезы для объёма, сверху — крышка со штриховкой и контур, который рисуется */}
					{Array.from({ length: WALL_SLICES }, (_, i) => (
						<WallSlice key={i} index={i} />
					))}
					<svg className="lp-layer lp-cap" viewBox={VIEWBOX} aria-hidden="true">
						{PLAN_WALLS.map((wall, i) => (
							<rect
								key={`${wall.x}-${wall.y}`}
								className="lp-fade lp-wall-fill"
								style={delay(0.9 + i * 0.03)}
								x={wall.x}
								y={wall.y}
								width={wall.w}
								height={wall.h}
							/>
						))}
						{PLAN_WALLS.map((wall, i) => (
							<rect
								key={`${wall.x}-${wall.y}-outline`}
								className="lp-draw lp-wall-outline"
								style={delay(0.15 + i * 0.05)}
								pathLength={1}
								x={wall.x}
								y={wall.y}
								width={wall.w}
								height={wall.h}
							/>
						))}
						{PLAN_WINDOWS.map((win, i) => (
							<g
								key={`${win.x}-${win.y}`}
								className="lp-fade lp-window"
								style={delay(1.1 + i * 0.06)}
							>
								<rect x={win.x} y={win.y} width={win.w} height={win.h} />
								<path
									d={
										win.w > win.h
											? `M${win.x} ${win.y + win.h / 2}h${win.w}`
											: `M${win.x + win.w / 2} ${win.y}v${win.h}`
									}
								/>
							</g>
						))}
					</svg>
				</div>

				{/* Рендер этой же квартиры из 3D-вида планировщика: снят камерой, повторяющей наклон
			    сцены, с запасом по краям, — поэтому ложится ровно поверх поднятых стен */}
				<img
					className="lp-render"
					src={HERO_RENDER}
					alt="Эта же квартира в 3D: мебель, отделка и свет"
					width={1680}
					height={1120}
					decoding="async"
				/>
			</div>

			<figcaption className="lp-stage-bar">
				<div className="lp-switch" role="group" aria-label="Вид чертежа">
					<button type="button" aria-pressed={view === "plan"} onClick={() => choose("plan")}>
						2D
					</button>
					<button type="button" aria-pressed={view === "3d"} onClick={() => choose("3d")}>
						3D
					</button>
				</div>
				<UIKit.Text as="span" className="lp-mono text-xs text-landing-muted">
					Общая площадь{" "}
					<UIKit.Text as="span" className="text-landing-text">
						{formatArea(totalArea())} м²
					</UIKit.Text>
				</UIKit.Text>
			</figcaption>
		</figure>
	);
};
